import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateKitZipMutation, useGetKitZipQuery } from '../../../../../redux/api/fymAPI';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { assetMessages } from './assetMessages';
import { trackAssetDownloaded } from '../../assetEvents';

/** How often the page asks for an emailed zip while the realtime socket is down. */
export const KIT_ZIP_POLL_MS = 10_000;

interface UseKitZipProps {
  accountId: number;
  catalogItemUuid: string;
}

export interface KitZip {
  download: () => void;
  isZipping: boolean;
  /** The address of the ready zip, else null. The toolbar shows it as a "Download the zip" link. */
  readyUrl: string | null;
}

/**
 * "Download all (.zip)": `POST .../zip`. A zip that is ready within 20 s answers its url. The
 * hook does not open it: a `window.open` after the wait is a pop-up that the browser blocks. The
 * toolbar shows the url as a link instead, and the user's click opens it. A larger kit answers
 * `{ status: 'emailing', requestId }`: the server emails the link, and the open page shows it too.
 * The `kit.zip.updated` event reads `GET .../zip/:requestId` again; only while the realtime
 * socket is down does the hook poll it.
 */
export function useKitZip({ accountId, catalogItemUuid }: UseKitZipProps): KitZip {
  const intl = useIntl();
  const { showInfo, showError } = useShowToast();
  const [createKitZip, { isLoading }] = useCreateKitZipMutation();
  const [readyUrl, setReadyUrl] = useState<string | null>(null);
  // The emailed request whose end the page waits for. Null: the page waits for no zip.
  const [requestId, setRequestId] = useState<string | null>(null);

  const isWaiting = requestId !== null;
  const pollingInterval = useRealtimePollInterval(isWaiting ? KIT_ZIP_POLL_MS : 0);
  const { currentData: emailed, error: emailedError } = useGetKitZipQuery(
    { accountId, catalogItemUuid, requestId: requestId ?? '' },
    { skip: !isWaiting, pollingInterval }
  );

  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    if (emailedError !== undefined) {
      // The email still carries the link: stop waiting with no message.
      setRequestId(null);
      return;
    }
    if (emailed?.status === 'ready') {
      setReadyUrl(getFullAPIFileURL(emailed.url));
      setRequestId(null);
    } else if (emailed?.status === 'failed') {
      setRequestId(null);
      showError(intl.formatMessage(assetMessages.zipFailed));
    }
  }, [emailed, emailedError, intl, isWaiting, showError]);

  const download = useCallback(() => {
    setReadyUrl(null);
    setRequestId(null);
    createKitZip({ accountId, catalogItemUuid })
      .unwrap()
      .then((result) => {
        if ('url' in result) {
          setReadyUrl(getFullAPIFileURL(result.url));
          return;
        }
        showInfo(intl.formatMessage(assetMessages.zipEmailing));
        setRequestId(result.requestId ?? null);
      })
      .catch((error: unknown) => {
        console.error('Failed to make the kit zip', error);
        showError(intl.formatMessage(assetMessages.zipFailed));
      });
  }, [accountId, catalogItemUuid, createKitZip, intl, showError, showInfo]);

  useEffect(() => {
    if (readyUrl !== null) {
      trackAssetDownloaded(undefined, 'zip');
    }
  }, [readyUrl]);

  return { download, isZipping: isLoading, readyUrl };
}
